import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import CircularLoader from "app/components/CircularLoader";
import { actions } from "app/containers/Features/containers/User/redux/slice";
import { ChevronDown } from "lucide-react";
import React, {
  cloneElement,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
} from "react";
import { useDispatch } from "react-redux";
import { CheckboxActiveIcon } from "../Icons";
import CheckboxIcon from "../Icons/CheckboxIcon";
import Input from "../Input";
import { MultiSelectDropdownProps } from "./type";

const MultiSelectDropdown: FC<MultiSelectDropdownProps> = ({
  title,
  options,
  selectedOptions,
  setSelectedOptions,
  searchPlaceholder = "Search...",
  showSearch = true,
  icon,
  searchKey,
  trigger,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  searchLoading = false,
  handleSelectAllFunction,
  handleUnselectAllFunction,
  selectOptionsFunction,
  isExternalSearch = false,
  externalSearchValue,
  setExternalSearchValue,
  contentClassName,
}) => {
  const dispatch = useDispatch();
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [searchActivity, setSearchActivity] = React.useState("");
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (!open || !showSearch) return;

    // Keep focus in search input to avoid DropdownMenu typeahead stealing focus.
    searchInputRef.current?.focus();
  }, [open, showSearch]);

  const isSelected =
    selectedOptions?.length === options?.length && options?.length > 0;
  const filteredOptions =
    options?.filter((option) =>
      option.value.toLowerCase().includes(searchActivity.toLowerCase()),
    ) ?? [];
  const updatedIcon = icon
    ? cloneElement(icon, { color: isSelected ? "#5A8DF1" : "#79808A" })
    : null;

  const noDataFound = options?.length === 0;

  const handleOptionToggle = (option: string) => {
    if (selectedOptions?.includes(option)) {
      setSelectedOptions(selectedOptions.filter((item) => item !== option));
    } else {
      setSelectedOptions([...(selectedOptions ?? []), option]);
    }
  };

  const handleSelectAll = () => {
    if (handleSelectAllFunction) {
      handleSelectAllFunction();
    } else {
      setSelectedOptions(options?.map((option) => option.id) ?? []);
    }
  };

  const handleUnselectAll = () => {
    if (handleUnselectAllFunction) {
      handleUnselectAllFunction();
    } else {
      setSelectedOptions([]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isExternalSearch && setExternalSearchValue) {
      setExternalSearchValue(e.target.value);
    } else {
      const term = e.target.value;
      setSearchActivity(term);
      if (searchKey) {
        dispatch(actions.setActivitiesFilter({ key: searchKey, value: term }));
      } else {
        console.error("searchKey is undefined. Ensure it is set correctly.");
      }
    }
  };

  const handleScroll = useCallback(() => {
    if (!listRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      onLoadMore?.();
    }
  }, [hasMore, isLoading, onLoadMore]);

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  return (
    <DropdownMenu.Root onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        {trigger ?? (
          <button
            className={`min-w-[255px] h-[32px] flex items-center justify-between px-2.5 py-0 gap-1.5 bg-white border rounded-full text-sm outline-none ${isSelected ? "border-[#5A8DF1] text-[#5A8DF1]" : "border-[#79808A] text-[#79808A]"}`}
          >
            <div className="flex items-center gap-2">
              {updatedIcon}
              <span>{title}</span>
            </div>
            <ChevronDown
              className={`ml-2 h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"} ${isSelected ? "text-[#5A8DF1]" : "text-[#79808A]"}`}
            />
          </button>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={`w-80 bg-white shadow-lg border border-gray-100 py-2 rounded-md z-50 mt-[2px] ${contentClassName ?? ""}`}
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="px-4 py-2 flex justify-between items-center text-md font-semibold border-b border-[#E9ECF2] text-[#79808A] sticky top-0 bg-white z-10">
          <span className="uppercase text-[14px]">{title}</span>
          <button
            className="text-[#5A8DF1] text-sm font-medium disabled:text-gray-300"
            disabled={noDataFound}
            onClick={isSelected ? handleUnselectAll : handleSelectAll}
          >
            {isSelected ? "Unselect All" : "Select All"}
          </button>
        </div>
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="max-h-[200px] overflow-y-auto "
        >
          {showSearch && (
            <div className="px-2 py-2">
              <Input
                id="search"
                placeholder={searchPlaceholder ?? "Search by project name"}
                value={isExternalSearch ? externalSearchValue : searchActivity}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                inputRef={searchInputRef}
                innerContainerClassName="px-2 pb-1 border-b-[1px] border-gray-100 "
              />
            </div>
          )}
          {searchLoading ? (
            <div className="w-full flex items-center justify-center py-3">
              <CircularLoader size="sm" />
            </div>
          ) : noDataFound ? (
            <div className="w-full flex items-center justify-center py-3">
              <span className="text-sm text-secondary">No options found</span>
            </div>
          ) : (
            <>
              {filteredOptions.map((option) => (
                <DropdownMenu.Item
                  key={option.id}
                  className="cursor-pointer hover:outline-none outline-none hover:bg-[#F4F5F7] px-3 py-4 flex items-center gap-2"
                  onSelect={() => {
                    if (selectOptionsFunction) {
                      selectOptionsFunction(option.id);
                    } else {
                      handleOptionToggle(option.id);
                    }
                  }}
                  style={{ border: "none" }}
                >
                  {selectedOptions?.includes(option.id) ? (
                    <div className="size-5">
                      <CheckboxActiveIcon size={20} />
                    </div>
                  ) : (
                    <div className="size-5">
                      <CheckboxIcon size={20} />
                    </div>
                  )}
                  <span className="text-secondary text-sm font-normal truncate">
                    {option.value}
                  </span>
                </DropdownMenu.Item>
              ))}
            </>
          )}
          {isLoading && filteredOptions.length > 0 && (
            <div className="w-full flex items-center justify-center py-3">
              <CircularLoader size="sm" />
            </div>
          )}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default MultiSelectDropdown;
